// Automated-rule zman keys
// -------------------------------------------------------------------------
// The Minyanim-Roll "Automated" tab anchors each rule's Start / End Time on a
// zman. That selector used to offer its own curated nine-entry list of
// snake_case values (alos_hashachar, netz, tzeis_hakochavim, …); it now offers
// exactly the same options as the Minyanim tab's Dynamic Zman dropdown
// (getMinyanimKosherZmanimOptions), i.e. canonical kosher-zmanim keys.
//
// Rules saved before that change still hold the legacy values, so every layer
// that reads a rule — the editor form, the editor's live preview and the live
// display — normalizes through {@link normalizeAutomatedZmanKey} first. Each
// legacy value maps to the canonical key that resolves to the SAME clock time
// the display already produced for it, so existing rolls keep their times.

export const LEGACY_AUTOMATED_ZMAN_KEYS: Record<string, string> = {
  alos_hashachar: 'alos72',
  netz: 'sunrise',
  sof_zman_shema: 'sofZmanShmaGRA',
  chatzos: 'chatzos',
  mincha_gedolah: 'minchaGedola',
  mincha_ketanah: 'minchaKetana',
  plag: 'plagHamincha',
  shkiah: 'sunset',
  // kosher-zmanim's plain getTzais() is the 8.5° Geonim tzeis, which is what
  // the legacy `tzais` lookup returned — same instant, canonical key.
  tzeis_hakochavim: 'tzaisGeonim8Point5Degrees',
};

// Zman a newly added Automated rule starts on. Keep this a BASIC-section option:
// Searchable_Zman_Dropdown opens showing every section (no "View more") whenever
// the selected zman is an Advanced/Special one, so an Advanced default would make
// the Automated tab's dropdown open expanded while the Minyanim tab's opens
// collapsed on Basic.
export const DEFAULT_AUTOMATED_ZMAN_KEY = 'tzais72';

/** Canonical kosher-zmanim key for a stored/selected automated-rule zman. */
export function normalizeAutomatedZmanKey(value: unknown): string {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  return LEGACY_AUTOMATED_ZMAN_KEYS[raw] || raw;
}

/**
 * A rule boundary's ± offset in whole minutes: positive shifts the anchor zman
 * later, NEGATIVE shifts it earlier (e.g. "Mincha, 20 minutes before shkiah" is
 * sunset with -20). Anything unparseable — an empty box, a lone "-" mid-typing —
 * reads as no offset.
 *
 * Shared so the editor form, the save payload, the editor preview and the live
 * display all coerce the value the same way, including the sign.
 */
export function normalizeAutomatedOffsetMinutes(value: unknown): number {
  const offset = Number(String(value ?? '').trim());
  return Number.isFinite(offset) ? Math.round(offset) : 0;
}

/**
 * Human wording for a boundary offset — "+ 10 min" / "− 10 min", '' when there
 * is none — so a negative offset never reads as the nonsensical "+ -10 min".
 */
export function formatAutomatedOffsetLabel(value: unknown): string {
  const offset = normalizeAutomatedOffsetMinutes(value);
  if (!offset) return '';
  return `${offset > 0 ? '+' : '−'} ${Math.abs(offset)} min`;
}
